import bankTransferImage from "@assets/images/payments/bank-transfer.png";
import cardImage from "@assets/images/payments/card.png";
import codeImage from "@assets/images/payments/code.png";
import fawryImage from "@assets/images/payments/fawry.png";
import instaPayImage from "@assets/images/payments/instapay.webp";
import vodafoneCashImage from "@assets/images/payments/vodafone-cash.png";
import { PaymentMethod } from "@core/invoices/types";
import { resolveInvoiceSubmission } from "@core/invoices/utils/invoice-submission";
import {
  getReceiptImageProblem,
  receiptProblemFromErrorCode,
  receiptProblemTranslationKey,
} from "@core/invoices/utils/receipt-image";
import { errorToast, successToast } from "@design-system/components/toast";
import { settingsAtom } from "@framework/shared/atoms";
import { type ActionResponse, useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { useNavigate } from "@remix-run/react";
import { useState } from "react";
import {
  FaCreditCard,
  FaMobileAlt,
  FaMoneyBill,
  FaQrcode,
  FaUniversity,
} from "react-icons/fa";
import { paymentAtom, paymentPortal } from "../atoms";
import { prepareReceiptImage } from "../utils/prepare-receipt-image";
export * from "./use-fawry-payment";

const paymentMethodConfig = {
  fawry: {
    name: "invoices.fawry",
    description: "invoices.fawryDescription",
    icon: FaMoneyBill,
    image: fawryImage,
    color: "bg-yellow-400",
  },
  card: {
    name: "invoices.card",
    description: "invoices.cardDescription",
    icon: FaCreditCard,
    image: cardImage,
    color: "bg-blue-500",
  },
  code: {
    name: "invoices.code",
    description: "invoices.codeDescription",
    icon: FaQrcode,
    image: codeImage,
    color: "bg-purple-500",
  },
  vodafoneCash: {
    name: "invoices.vodafoneCash",
    description: "invoices.vodafoneCashDescription",
    icon: FaMobileAlt,
    image: vodafoneCashImage,
    color: "bg-red-500",
  },
  instaPay: {
    name: "invoices.instaPay",
    description: "invoices.instaPayDescription",
    icon: FaMobileAlt,
    image: instaPayImage,
    color: "bg-green-500",
  },
  bankTransfer: {
    name: "invoices.bankTransfer",
    description: "invoices.bankTransferDescription",
    icon: FaUniversity,
    image: bankTransferImage,
    color: "bg-gray-600",
  },
};

export const usePaymentMethods = () => {
  const paymentMethods = settingsAtom.use("general")?.paymentMethods || [];

  return {
    paymentMethods,
    paymentMethodConfig,
    get paymentMethodsList() {
      return paymentMethods.map((method) => {
        return {
          method,
          ...paymentMethodConfig[method],
        };
      });
    },
  };
};

export function useCurrentPaymentMethod() {
  const paymentMethod = paymentAtom.use("paymentMethod");

  const changePaymentMethod = (method: PaymentMethod) => {
    if (paymentAtom.get("paymentMethod") === method) return;

    // A code typed on the code tab or a receipt picked for another method
    // must not travel with the new method (a leftover code used to replace
    // a Vodafone Cash purchase with a code redemption).
    paymentAtom.merge({
      paymentMethod: method,
      code: undefined,
      receipt: undefined,
      canSubmit: false,
    });
  };

  return { paymentMethod, changePaymentMethod };
}

export const usePaymentSettings = () => {
  const bankTransferSettings = settingsAtom.use("bankTransfer");
  const mobileWalletsSettings = settingsAtom.use("general");

  return {
    bankTransferSettings,
    mobileWalletsSettings,
  };
};

export const usePaymentPortalData = () => {
  const lessons = paymentAtom.use("lessons") || [];
  const offers = paymentAtom.use("offers") || [];
  const isOpened = paymentPortal.useOpened();

  const lessonsCost = lessons.reduce((sum, lesson) => sum + lesson.cost, 0);
  const offersCost = offers.reduce((sum, offer) => sum + offer.price, 0);
  const totalCost = lessonsCost + offersCost;
  
  const lessonsCount = lessons.length;
  const offersCount = offers.length;
  const totalItems = lessonsCount + offersCount;

  return {
    lessons,
    offers,
    totalCost,
    lessonsCount,
    offersCount,
    totalItems,
    isOpened,
  };
};

export function useCodePayment() {
  const updateCode = (code: string) => {
    paymentAtom.merge({
      code,
      canSubmit: code?.length > 1,
    });
  };

  return {
    updateCode,
  };
}

/**
 * Message to show for a failed checkout: a receipt problem in the student's
 * language, the server's own message, or a generic translated fallback
 * (never axios' "Request failed with status code 500").
 */
function paymentErrorMessage(response: ActionResponse) {
  const data = (response.data ?? {}) as { code?: unknown; error?: unknown };
  const receiptProblem = receiptProblemFromErrorCode(data.code);

  if (receiptProblem) return trans(receiptProblemTranslationKey(receiptProblem));

  if (typeof data.error === "string" && data.error) return data.error;

  return trans("invoices.paymentFailed");
}

export function useSubmitPayment() {
  const createInvoice = useAction("/invoices");
  const navigate = useNavigate();

  const submit = async () => {
    const paymentMethod = paymentAtom.get("paymentMethod");

    if (!paymentMethod) return;

    // Only handle manual payment methods here
    if (["fawry", "card"].includes(paymentMethod)) {
      return;
    }

    const plan = resolveInvoiceSubmission({
      paymentMethod,
      code: paymentAtom.get("code"),
    });
    const receipt = paymentAtom.get("receipt");

    if (plan.kind === "invoice" && plan.requiresReceipt) {
      const receiptProblem = getReceiptImageProblem(receipt);

      if (receiptProblem) {
        errorToast(trans(receiptProblemTranslationKey(receiptProblem)));
        return;
      }
    }

    paymentAtom.change("state", "processing");

    const lessonsIds = paymentAtom.get("lessons") || [];
    const offersIds = paymentAtom.get("offers") || [];

    const data = new FormData();

    for (const lesson of lessonsIds) {
      data.append("lessons", lesson.id.toString());
    }

    for (const offer of offersIds) {
      data.append("offers", offer.id.toString());
    }

    data.append("paymentMethod", paymentMethod);

    // Send only what the chosen method needs.
    if (plan.kind === "code") {
      data.append("code", plan.code);
    } else if (receipt) {
      data.append("receiptImage", receipt, receipt.name);
    }

    const response = await createInvoice(data);

    if (response.isOK) {
      navigate(0);
      
      // Show appropriate success message based on what was purchased
      if (lessonsIds.length > 0 && offersIds.length > 0) {
        successToast(trans("checkout.purchaseSuccessful"));
      } else if (lessonsIds.length > 0) {
        successToast(trans("grades.lessonUnlocked"));
      } else if (offersIds.length > 0) {
        successToast(trans("offers.offerPurchased"));
      }
      
      paymentAtom.reset();
      paymentPortal.reset();
    } else {
      errorToast(paymentErrorMessage(response));
      paymentAtom.change("state", "idle");
    }
  };

  const canSubmit = paymentAtom.use("canSubmit");
  const isProcessing = paymentAtom.use("state") === "processing";

  return {
    submit,
    canSubmit,
    isProcessing,
  };
}

export function usePaymentMethodCopyField() {
  const [copiedField, setCopiedField] = useState<string>();

  const onCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(undefined), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return {
    copiedField,
    onCopy,
  };
}

// const [copiedField, setCopiedField] = useState<string | null>(null);
export const usePaymentMethodReceiptUpload = () => {
  const receiptFile = paymentAtom.use("receipt");
  const [isPreparing, setIsPreparing] = useState(false);

  const uploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    // Let the student pick the same file again after an error.
    input.value = "";

    if (!file) return;

    paymentAtom.merge({ canSubmit: false, receipt: undefined });
    setIsPreparing(true);

    try {
      const prepared = await prepareReceiptImage(file);

      if (prepared.problem) {
        errorToast(trans(receiptProblemTranslationKey(prepared.problem)));
        return;
      }

      paymentAtom.merge({
        canSubmit: true,
        receipt: prepared.file,
      });
    } finally {
      setIsPreparing(false);
    }
  };

  const removeFile = () => {
    paymentAtom.merge({
      canSubmit: false,
      receipt: undefined,
    });
  };

  // const handleCopyToClipboard = async (text: string, fieldName: string) => {
  //   try {
  //     await navigator.clipboard.writeText(text);
  //     setCopiedField(fieldName);
  //     setTimeout(() => setCopiedField(null), 2000);
  //   } catch (err) {
  //     console.error("Failed to copy text: ", err);
  //   }
  // };

  return {
    receiptFile,
    isPreparing,
    // copiedField,
    uploadFile,
    removeFile,
    // handleCopyToClipboard,
  };
};

export const usePaymentMethodConfig = () => {
  const paymentMethodConfig = {
    fawry: {
      name: "invoices.fawry",
      description: "invoices.fawryDescription",
      requiresReceipt: false,
    },
    card: {
      name: "invoices.card",
      description: "invoices.cardDescription",
      requiresReceipt: false,
    },
    code: {
      name: "invoices.code",
      description: "invoices.codeDescription",
      requiresReceipt: false,
    },
    vodafoneCash: {
      name: "invoices.vodafoneCash",
      description: "invoices.vodafoneCashDescription",
      requiresReceipt: true,
    },
    instaPay: {
      name: "invoices.instaPay",
      description: "invoices.instaPayDescription",
      requiresReceipt: true,
    },
    bankTransfer: {
      name: "invoices.bankTransfer",
      description: "invoices.bankTransferDescription",
      requiresReceipt: true,
    },
  };

  return paymentMethodConfig;
};
